import { Link, useParams } from 'react-router-dom';
import { PageHeader, Card, Badge, Button, MarkdownView, ReadAloud, IconButton, MediaImage } from '../ui';
import { mediaUrl } from '../../api/client';
import { useBinder } from '../../lib/useBinder';
import { useRunStore } from '../../store/runStore';
import { ShowButton } from '../common/ShowButton';
import { Backlinks } from '../common/Backlinks';
import shared from './sections.module.css';
import styles from './RoomsSection.module.css';

/** Room list grouped by floor, with visited tracking. */
export function RoomsSection() {
  const ctx = useBinder();
  const toggleVisited = useRunStore((s) => s.toggleVisited);
  if (!ctx) return null;
  const { adventure, state, instance, slug } = ctx;
  const { floors, rooms, mapImage, mapCaption } = adventure.rooms;

  const floorList = floors.length
    ? [...floors].sort((a, b) => b.order - a.order)
    : [{ id: '', name: 'Stanze', order: 0 }];

  const visitedCount = state.visitedRooms.length;

  return (
    <div>
      <PageHeader
        eyebrow="Stanze & Mappa"
        title="Stanze"
        subtitle={`${visitedCount} / ${rooms.length} visitate`}
      />

      {mapImage && (
        <Card className={styles.mapCard}>
          <MediaImage
            src={mediaUrl(slug, mapImage)}
            alt={mapCaption || 'Mappa'}
            fit="contain"
            className={styles.mapImage}
          />
          <div className={styles.mapFoot}>
            {mapCaption && <span className={styles.mapCaption}>{mapCaption}</span>}
            <ShowButton
              slug={slug}
              image={mapImage}
              title="Mappa"
              caption={mapCaption}
              instanceId={instance.id}
            />
          </div>
        </Card>
      )}

      {floorList.map((floor) => {
        const inFloor = rooms.filter((r) => (r.floor ?? '') === floor.id);
        if (!inFloor.length) return null;
        return (
          <div key={floor.id || 'all'} className={styles.floor}>
            <h2 className={styles.floorName}>{floor.name}</h2>
            <div className={styles.rooms}>
              {inFloor.map((room) => {
                const visited = state.visitedRooms.includes(room.id);
                return (
                  <Card
                    key={room.id}
                    padded={false}
                    className={[styles.room, visited ? shared.visited : undefined]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={styles.roomTop}>
                      <span className={styles.roomIcon} aria-hidden>
                        {room.icon ?? '🚪'}
                      </span>
                      <Link to={`/run/${instance.id}/rooms/${room.id}`} className={styles.roomName}>
                        {room.name}
                      </Link>
                      <IconButton
                        label={visited ? 'Segna come non visitata' : 'Segna come visitata'}
                        size="sm"
                        active={visited}
                        onClick={() => toggleVisited(room.id)}
                      >
                        {visited ? '✓' : '○'}
                      </IconButton>
                    </div>
                    {room.description && (
                      <p className={styles.roomDesc}>
                        {room.description.slice(0, 120)}
                        {room.description.length > 120 ? '…' : ''}
                      </p>
                    )}
                    <div className={styles.roomFoot}>
                      {room.event && <Badge tone="ember">Evento</Badge>}
                      {room.secret && <Badge tone="warn">Segreto</Badge>}
                      <Link to={`/run/${instance.id}/rooms/${room.id}`} className={styles.detailBtn}>
                        Dettagli →
                      </Link>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Single room detail. */
export function RoomDetail() {
  const ctx = useBinder();
  const { entityId } = useParams();
  const toggleVisited = useRunStore((s) => s.toggleVisited);
  if (!ctx) return null;
  const { adventure, state, instance, slug } = ctx;
  const room = adventure.rooms.rooms.find((r) => r.id === entityId);

  if (!room) {
    return (
      <div>
        <Link to={`/run/${instance.id}/rooms`}>← Stanze</Link>
        <p>Stanza non trovata.</p>
      </div>
    );
  }

  const visited = state.visitedRooms.includes(room.id);
  const connected = (room.connections ?? [])
    .map((id) => adventure.rooms.rooms.find((r) => r.id === id))
    .filter(Boolean);
  const roomItems = (room.items ?? [])
    .map((id) => adventure.items.find((i) => i.id === id))
    .filter(Boolean);
  const roomClues = (room.clues ?? [])
    .map((id) => adventure.clues.find((c) => c.id === id))
    .filter(Boolean);

  return (
    <div>
      <Link to={`/run/${instance.id}/rooms`} className={styles.back}>
        ← Tutte le stanze
      </Link>
      <PageHeader
        eyebrow={room.icon}
        title={room.name}
        actions={
          <>
            <Button
              variant={visited ? 'default' : 'primary'}
              onClick={() => toggleVisited(room.id)}
              icon={visited ? '✓' : '○'}
            >
              {visited ? 'Visitata' : 'Segna visitata'}
            </Button>
            <ShowButton slug={slug} title={room.name} instanceId={instance.id} />
          </>
        }
      />

      <div className={shared.stack}>
        {room.readAloud && <ReadAloud>{room.readAloud}</ReadAloud>}

        {room.description && (
          <Card>
            <MarkdownView>{room.description}</MarkdownView>
          </Card>
        )}

        {room.event && (
          <Card className={styles.eventCard}>
            <div className={shared.blockLabel}>⚡ Evento / Atmosfera</div>
            <MarkdownView>{room.event}</MarkdownView>
          </Card>
        )}

        {!!(room.details && room.details.length) && (
          <Card>
            <div className={shared.blockLabel}>Dettagli</div>
            <ul className={styles.details}>
              {room.details.map((d, i) => (
                <li key={i}>
                  <MarkdownView inline>{d}</MarkdownView>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {typeof room.secret === 'string' && room.secret && (
          <Card className={styles.secretCard}>
            <div className={shared.blockLabel}>🤫 Segreto</div>
            <MarkdownView>{room.secret}</MarkdownView>
          </Card>
        )}

        {!!roomItems.length && (
          <Card>
            <div className={shared.blockLabel}>Oggetti qui</div>
            <div className={shared.linkRow}>
              {roomItems.map((i) => (
                <Link
                  key={i!.id}
                  to={`/run/${instance.id}/items?focus=${i!.id}`}
                  className={shared.chip}
                >
                  {i!.icon ?? '🗝️'} {i!.name}
                </Link>
              ))}
            </div>
          </Card>
        )}

        {!!roomClues.length && (
          <Card>
            <div className={shared.blockLabel}>Indizi qui</div>
            <div className={shared.linkRow}>
              {roomClues.map((c) => (
                <Link
                  key={c!.id}
                  to={`/run/${instance.id}/clues?focus=${c!.id}`}
                  className={shared.chip}
                >
                  {c!.icon ?? '📜'} {c!.title}
                </Link>
              ))}
            </div>
          </Card>
        )}

        {!!connected.length && (
          <Card>
            <div className={shared.blockLabel}>Collegamenti</div>
            <div className={shared.linkRow}>
              {connected.map((r) => (
                <Link
                  key={r!.id}
                  to={`/run/${instance.id}/rooms/${r!.id}`}
                  className={shared.chip}
                >
                  {r!.icon ?? '🚪'} {r!.name}
                </Link>
              ))}
            </div>
          </Card>
        )}

        <Backlinks adventure={adventure} targetRef={`place:${room.id}`} />
      </div>
    </div>
  );
}
